import { t } from "../i18n";

/**
 * Всплывающие подсказки (popover) для иконок «?».
 *
 * Любой элемент с атрибутом `data-help="<i18n-ключ>"` при наведении/фокусе
 * показывает плавающее окно с текстом подсказки. Окно монтируется в
 * `document.body` с `position: fixed`, чтобы его не обрезал `overflow`
 * боковой панели (`.side-rail-panels`). Текст берётся через `t()` в момент
 * показа — поэтому подсказки автоматически следуют за сменой языка.
 */

let popoverEl: HTMLDivElement | null = null;
let activeIcon: HTMLElement | null = null;
let showTimer: number | null = null;
let hideTimer: number | null = null;

const SHOW_DELAY = 200;
const HIDE_DELAY = 140;
const MARGIN = 8;

function clearTimers(): void {
  if (showTimer !== null) {
    window.clearTimeout(showTimer);
    showTimer = null;
  }
  if (hideTimer !== null) {
    window.clearTimeout(hideTimer);
    hideTimer = null;
  }
}

function ensurePopoverEl(): HTMLDivElement {
  if (!popoverEl) {
    const el = document.createElement("div");
    el.className = "help-popover";
    el.setAttribute("role", "tooltip");
    el.hidden = true;
    // Удержание курсора над самим окном не должно его закрывать.
    el.addEventListener("mouseenter", () => {
      if (hideTimer !== null) {
        window.clearTimeout(hideTimer);
        hideTimer = null;
      }
    });
    el.addEventListener("mouseleave", scheduleHide);
    document.body.appendChild(el);
    popoverEl = el;
  }
  return popoverEl;
}

function positionPopover(icon: HTMLElement): void {
  const el = ensurePopoverEl();
  const rect = icon.getBoundingClientRect();
  const pw = el.offsetWidth;
  const ph = el.offsetHeight;
  const vw = window.innerWidth;
  const vh = window.innerHeight;

  // По умолчанию — справа от иконки.
  let left = rect.right + MARGIN;
  let top = rect.top - 2;

  // Не помещается справа — пробуем слева.
  if (left + pw > vw - MARGIN) {
    left = rect.left - MARGIN - pw;
  }
  // Не помещается и слева — показываем снизу, прижав по горизонтали к окну.
  if (left < MARGIN) {
    left = Math.min(Math.max(MARGIN, rect.left), vw - MARGIN - pw);
    top = rect.bottom + MARGIN;
  }
  // Вертикальная коррекция, чтобы не уезжало за низ/верх экрана.
  if (top + ph > vh - MARGIN) {
    top = Math.max(MARGIN, vh - MARGIN - ph);
  }
  if (top < MARGIN) {
    top = MARGIN;
  }

  el.style.left = `${Math.round(left)}px`;
  el.style.top = `${Math.round(top)}px`;
}

function showFor(icon: HTMLElement): void {
  const key = icon.getAttribute("data-help");
  if (!key) {
    return;
  }
  const el = ensurePopoverEl();
  el.textContent = t(key);
  el.hidden = false;
  activeIcon = icon;
  positionPopover(icon);
}

function scheduleShow(icon: HTMLElement): void {
  clearTimers();
  showTimer = window.setTimeout(() => {
    showTimer = null;
    showFor(icon);
  }, SHOW_DELAY);
}

function hideNow(): void {
  clearTimers();
  if (popoverEl) {
    popoverEl.hidden = true;
  }
  activeIcon = null;
}

function scheduleHide(): void {
  if (showTimer !== null) {
    window.clearTimeout(showTimer);
    showTimer = null;
  }
  if (hideTimer !== null) {
    window.clearTimeout(hideTimer);
  }
  hideTimer = window.setTimeout(() => {
    hideTimer = null;
    if (popoverEl) {
      popoverEl.hidden = true;
    }
    activeIcon = null;
  }, HIDE_DELAY);
}

function closestHelp(target: EventTarget | null): HTMLElement | null {
  if (!(target instanceof Element)) {
    return null;
  }
  return target.closest<HTMLElement>("[data-help]");
}

export function initHelpPopovers(): void {
  ensurePopoverEl();

  document.addEventListener("mouseover", (event) => {
    const icon = closestHelp(event.target);
    if (icon) {
      scheduleShow(icon);
    }
  });
  document.addEventListener("mouseout", (event) => {
    const icon = closestHelp(event.target);
    if (icon) {
      scheduleHide();
    }
  });
  document.addEventListener("focusin", (event) => {
    const icon = closestHelp(event.target);
    if (icon) {
      showFor(icon);
    }
  });
  document.addEventListener("focusout", (event) => {
    const icon = closestHelp(event.target);
    if (icon) {
      scheduleHide();
    }
  });
  // Тач-устройства не генерируют надёжный hover (mouseover может не прийти
  // вовсе или "залипнуть" после тапа) — обрабатываем подсказку явным тапом:
  // тап по иконке открывает/закрывает её, тап снаружи — закрывает.
  document.addEventListener("click", (event) => {
    const icon = closestHelp(event.target);
    if (icon) {
      clearTimers();
      if (popoverEl && !popoverEl.hidden && activeIcon === icon) {
        hideNow();
      } else {
        showFor(icon);
      }
      event.stopPropagation();
      return;
    }
    if (popoverEl && !popoverEl.hidden && !popoverEl.contains(event.target as Node)) {
      hideNow();
    }
  });
  document.addEventListener("keydown", (event) => {
    if (event.key === "Escape") {
      hideNow();
    }
  });
  // Прокрутка/ресайз сбивают позицию — проще скрыть.
  window.addEventListener("scroll", hideNow, true);
  window.addEventListener("resize", hideNow);
}
